import { ICommonSettings, IOpenAISetting } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigOpenAISettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingOpenAI?: IOpenAISetting;
}

export interface IConfigOpenAISettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingOpenAIId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigOpenAISettings extends React.PureComponent<IConfigOpenAISettingsProps, IConfigOpenAISettingsState> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigOpenAISettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle"
    };
  }

  componentDidMount() {
    this.loadOpenAISetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigOpenAISettingsProps) {
    if (prevProps.editingOpenAIId !== this.props.editingOpenAIId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadOpenAISetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadOpenAISetting();
    }
  }

  loadOpenAISetting = () => {
    if (!this.props.editingOpenAIId) return;

    const editingOpenAI = this.props.commonSettings.openAISettings?.find(
      (x) => x.id === this.props.editingOpenAIId && !x.isGlobalCommonConfigOnProjectConfig
    );

    if (!editingOpenAI) return;
    this.setState({ editingOpenAI }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingOpenAI);
    });
  };

  existingNameValidator = async (_rule, value) => {
    const openAIsSettings = this.props.commonSettings?.openAISettings;

    const isSettingAlreadyExist =
      openAIsSettings?.some(
        (x) => x.name === value && x.id !== this.props.editingOpenAIId && !x.isGlobalCommonConfigOnProjectConfig
      ) || value === "Default";

    if (isSettingAlreadyExist) throw new Error("Open AI Settings Name is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();

    let obj: IOpenAISetting;
    if (this.props.editingOpenAIId) {
      obj = {
        id: this.props.editingOpenAIId,
        name: data.name,
        apiKey: data.apiKey
      };
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "openAISettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      obj = {
        id: Guid.create().toString(),
        name: data.name,
        apiKey: data.apiKey?.trim()
      };
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "openAISettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteOpenAI = () => {
    if (!this.props.editingOpenAIId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingOpenAIId || "",
      configKey: "openAISettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Open AI setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Open AI Setting"
        secondHeader="Open AI Setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingOpenAIId ? this.deleteOpenAI : undefined}
            submitLabel={this.props.editingOpenAIId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configOpenAiForm"
          />
        }
      >
        <Form
          id="configOpenAiForm"
          name="openAISetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription("appsettings_openaisettingpop_up_description")}
          >
            <ConfigField label="Name" required tooltipContentKey="appsettings_openaisettingpop_up_name">
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: "Please enter a Open AI Setting Name." },
                  { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Open AI Setting Name is invalid" },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Api Key" required tooltipContentKey="appsettings_openaisettingpop_up_apikey">
              <Form.Item name="apiKey" rules={[{ required: true, message: "Please enter a Api Key." }]}>
                <KuikaInput size="md" />
              </Form.Item>
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
